package it.unibo.tuprolog.solve.libs.io.primitives

import it.unibo.tuprolog.core.Term
import it.unibo.tuprolog.solve.ExecutionContext
import it.unibo.tuprolog.solve.libs.io.primitives.IOPrimitiveUtils.ensuringArgumentIsVarOrStream
import it.unibo.tuprolog.solve.primitive.Solve
import it.unibo.tuprolog.solve.primitive.UnaryPredicate

/**
 * Implements ISO's `current_output/1`: unifies the argument with the `$stream(...)` term of the current output
 * stream (the context's current output, or its standard output if none was [SetOutput]) if the argument is unbound;
 * if it is already bound to a `$stream(...)` term, succeeds iff it already denotes that same channel.
 *
 * @throws it.unibo.tuprolog.solve.exception.error.ExistenceError if the argument names a well-formed stream term
 * for which no channel is currently open.
 * @throws it.unibo.tuprolog.solve.exception.error.DomainError (`stream_or_alias`) if it is bound to something other
 * than a `$stream(...)` term.
 */
object CurrentOutput : UnaryPredicate.NonBacktrackable<ExecutionContext>("current_output") {
    override val help: String =
        """
        `current_output(?Stream)`
        
        Unifies `Stream` with the `${'$'}stream(out, Id)` term of the current output stream (standard output, unless changed via `set_output/1`); if `Stream` is already a stream term, it succeeds iff that term denotes the current output stream. It is deterministic. It raises an existence error if `Stream` is a stream term denoting no open stream, and a domain error (`stream_or_alias`) if it is bound to anything else, aliases included.

        **Examples**

        ```prolog
        ?- current_output(S), stream_property(S, alias(user_output)).
        yes.

        ?- current_output(S), write(S, hello).
        % prints: hello
        yes.

        ?- current_output(user_output).
        throws error(domain_error(stream_or_alias, user_output), _).
        ```
        """.trimIndent()

    override fun Solve.Request<ExecutionContext>.computeOne(first: Term): Solve.Response {
        val channel = ensuringArgumentIsVarOrStream(0)
        val currentChannel = context.outputChannels.let { it.current ?: it.stdOut }
        return when (channel) {
            null -> replyWith(mgu(first, currentChannel.streamTerm))
            currentChannel -> replySuccess()
            else -> replyFail()
        }
    }
}
